import React, { useState } from 'react';
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Logout as LogoutIcon,
  Flag as PushPinIcon,
  NotificationsNone as NotificationsNoneIcon,
} from '../../ui/icons';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../LanguageSwitcher';
import { useNotifications } from '../../../context/NotificationContext';
import NotificationPanel from '../../notifications/NotificationPanel';
import AssistantLauncher from '../../assistant/AssistantLauncher';

interface SidebarActionsProps {
  collapsed: boolean;
  pinned: boolean;
  isDarkMode: boolean;
  onToggleTheme: () => void;
  onLogout: () => void;
  onTogglePin: () => void;
}

const SidebarActions: React.FC<SidebarActionsProps> = ({
  collapsed,
  pinned,
  isDarkMode,
  onToggleTheme,
  onLogout,
  onTogglePin,
}) => {
  const { t, i18n } = useTranslation();
  const { unreadCount } = useNotifications();
  const [notifAnchor, setNotifAnchor] = useState<HTMLElement | null>(null);

  const actionBtn = [
    'relative flex items-center min-h-[44px] rounded-[12px] w-full border-0 p-0 cursor-pointer bg-transparent',
    'text-muted transition-all duration-300',
    'hover:translate-x-1 hover:text-primary hover:bg-primary/10',
  ].join(' ');

  const iconWrap = (extraClass = '') =>
    [
      'flex items-center justify-center flex-shrink-0',
      'transition-[margin] duration-300 ease-in-out',
      extraClass,
    ].join(' ');

  const labelCls = (collapsed: boolean) =>
    [
      'text-sm font-medium whitespace-nowrap overflow-hidden',
      'transition-all duration-300 ease-in-out',
      // `w-0` when collapsed so the hidden label takes no horizontal space and
      // the icon stays centred (see SidebarMenu for the same fix).
      collapsed ? 'w-0 opacity-0 -translate-x-5' : 'opacity-100 translate-x-0',
    ].join(' ');

  return (
    <div className="mt-auto px-2 pb-2 flex flex-col">
      <div className={`flex flex-col gap-1 ${collapsed ? 'items-center' : 'items-stretch'}`}>

        {/* Notifications */}
        <button
          onClick={(e) => setNotifAnchor(e.currentTarget)}
          title={collapsed ? t('notifications.title') : undefined}
          className={`${actionBtn} ${collapsed ? 'justify-center px-2' : 'justify-start px-3'}`}
        >
          <span className={iconWrap(collapsed ? 'mr-0' : 'mr-2.5')}>
            <NotificationsNoneIcon size={20} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 rounded-full bg-primary text-white text-[0.6rem] font-bold flex items-center justify-center shadow-pop">
                {unreadCount}
              </span>
            )}
          </span>
          <span className={labelCls(collapsed)}>{t('notifications.title')}</span>
        </button>

        {/* AI assistant */}
        <AssistantLauncher variant="sidebar" collapsed={collapsed} />

        {/* Theme toggle */}
        <button
          onClick={onToggleTheme}
          title={collapsed ? (isDarkMode ? t('menu.light_mode') : t('menu.dark_mode')) : undefined}
          className={`${actionBtn} ${collapsed ? 'justify-center px-2' : 'justify-start px-3'}`}
        >
          <span className={iconWrap(collapsed ? 'mr-0' : 'mr-2.5')}>
            {isDarkMode
              ? <LightModeIcon size={20} />
              : <DarkModeIcon size={20} />}
          </span>
          <span className={labelCls(collapsed)}>
            {isDarkMode ? t('menu.light_mode') : t('menu.dark_mode')}
          </span>
        </button>

        {/* Logout */}
        <button
          onClick={onLogout}
          title={collapsed ? t('menu.logout') : undefined}
          className={`${actionBtn} ${collapsed ? 'justify-center px-2' : 'justify-start px-3'}`}
        >
          <span className={iconWrap(collapsed ? 'mr-0' : 'mr-2.5')}>
            <LogoutIcon size={20} />
          </span>
          <span className={labelCls(collapsed)}>{t('menu.logout')}</span>
        </button>
      </div>

      {/* Language switcher + Pin row */}
      <div className="relative flex items-center gap-1 mt-2 mb-1 h-10">
        {/* Full language switcher — visible when expanded */}
        <div
          className={[
            'flex-1 transition-all duration-300 ease-in-out',
            collapsed ? 'opacity-0 pointer-events-none -translate-x-5' : 'opacity-100 translate-x-0',
          ].join(' ')}
        >
          <LanguageSwitcher />
        </div>

        {/* Compact flag toggle — visible when collapsed */}
        <button
          onClick={() => {
            const newLang = i18n.language === 'en' ? 'sk' : 'en';
            i18n.changeLanguage(newLang);
            localStorage.setItem('i18nextLng', newLang);
          }}
          className={[
            'absolute left-1/2 top-1/2 w-10 h-10 flex items-center justify-center border-0 p-0',
            'rounded-[12px] border border-primary/20',
            'bg-surface/80 cursor-pointer',
            'backdrop-blur-[10px] transition-all duration-300 ease-in-out',
            'hover:bg-primary/10',
            'hover:border-primary/30',
            'hover:shadow-card-hover',
            collapsed
              ? '-translate-x-1/2 -translate-y-1/2 opacity-100'
              : 'translate-x-[-calc(50%-20px)] -translate-y-1/2 opacity-0 pointer-events-none',
          ].join(' ')}
        >
          <img
            loading="lazy"
            width="20"
            height="15"
            src={i18n.language === 'en' ? 'https://flagcdn.com/gb.svg' : 'https://flagcdn.com/sk.svg'}
            alt={i18n.language === 'en' ? t('common.english_flag') : t('common.slovak_flag')}
            className="rounded-sm object-cover"
          />
        </button>

        {/* Pin button — only when expanded */}
        {!collapsed && (
          <button
            title={pinned ? t('menu.unpin_sidebar') : t('menu.pin_sidebar')}
            onClick={onTogglePin}
            className={[
              'flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-[12px]',
              'border cursor-pointer transition-all duration-300 ease-in-out',
              'hover:shadow-card-hover hover:scale-105',
              'hover:border-primary/30',
              pinned
                ? [
                    'bg-primary/15',
                    'border-primary/30',
                    'text-primary',
                  ].join(' ')
                : [
                    'bg-surface/80',
                    'border-primary/20',
                    'text-muted',
                    'hover:text-primary hover:bg-primary/10',
                  ].join(' '),
            ].join(' ')}
          >
            {pinned
              ? <PushPinIcon size={18} className="fill-current" />
              : <PushPinIcon size={18} />}
          </button>
        )}
      </div>

      <NotificationPanel
        anchorEl={notifAnchor}
        onClose={() => setNotifAnchor(null)}
      />
    </div>
  );
};

export default SidebarActions;
